import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

import { PRIMARY_BUTTON_SX, SECONDARY_BUTTON_SX } from "../../theme/buttonstyles";
import { BRAND_BLUE, BRAND_GREEN } from "../../theme/colors";

const EMPTY = {
  name: "",
  member_id: "",
  event_id: "",
  sponsorship_type: "",
  sponsorship_level: "",
  status: "prospect",
  amount: "",
  start_date: "",
  end_date: "",
  program_area: "",
  logo_placement: false,
  speaking_opportunity: false,
  table_or_booth: false,
  email_mention: false,
  social_media_mention: false,
  website_listing: false,
  attendee_list_access: false,
  notes: "",
};

const STATUS_OPTIONS = ["prospect", "committed", "invoiced", "paid", "fulfilled", "cancelled"];

const BENEFIT_OPTIONS = [
  ["logo_placement", "Logo placement"],
  ["speaking_opportunity", "Speaking opportunity"],
  ["table_or_booth", "Table or booth"],
  ["email_mention", "Email mention"],
  ["social_media_mention", "Social media mention"],
  ["website_listing", "Website listing"],
  ["attendee_list_access", "Attendee list access"],
];

const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    color: BRAND_BLUE,
    background: "#ffffff",
    "& fieldset": { border: "1px solid #cbd5e1" },
    "&:hover fieldset": { borderColor: BRAND_BLUE },
    "&.Mui-focused fieldset": { borderColor: BRAND_BLUE, borderWidth: 2 },
  },
  "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused": {
    color: "#64748b",
    fontWeight: 600,
  },
};

// MUI v7's Grid dropped the item/xs/sm/md breakpoint props in favor of `size`;
// a plain CSS grid avoids relying on that API and keeps spans explicit.
const FORM_ROW_SX = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(12, 1fr)" },
  gap: "16px",
};

function Field({ span, children }) {
  return <Box sx={{ gridColumn: { xs: "span 1", sm: `span ${span}` } }}>{children}</Box>;
}

const BENEFITS_GRID_SX = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
  columnGap: 2,
  rowGap: 0.5,
};

const BENEFIT_LABEL_SX = {
  "& .MuiFormControlLabel-label": { color: BRAND_BLUE, fontWeight: 600, fontSize: 14 },
};

function SectionHeader({ children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
      <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: BRAND_GREEN }} />
      <Typography sx={{ color: BRAND_BLUE, fontWeight: 800 }}>{children}</Typography>
    </Box>
  );
}

function payload(values) {
  return {
    name: values.name,
    member_id: Number(values.member_id),
    event_id: values.event_id ? Number(values.event_id) : null,
    sponsorship_type: values.sponsorship_type || null,
    sponsorship_level: values.sponsorship_level || null,
    status: values.status,
    amount: values.amount === "" ? null : Number(values.amount),
    start_date: values.start_date || null,
    end_date: values.end_date || null,
    program_area: values.program_area || null,
    logo_placement: values.logo_placement,
    speaking_opportunity: values.speaking_opportunity,
    table_or_booth: values.table_or_booth,
    email_mention: values.email_mention,
    social_media_mention: values.social_media_mention,
    website_listing: values.website_listing,
    attendee_list_access: values.attendee_list_access,
    notes: values.notes || null,
  };
}

export default function MembershipSponsorshipDialog({
  open,
  sponsorship,
  members,
  events,
  saving,
  onClose,
  onSubmit,
}) {
  const [values, setValues] = useState(EMPTY);

  useEffect(() => {
    if (!open) return;
    setValues({
      ...EMPTY,
      ...Object.fromEntries(Object.entries(sponsorship || {}).map(([k, v]) => [k, v ?? ""])),
      logo_placement: Boolean(sponsorship?.logo_placement),
      speaking_opportunity: Boolean(sponsorship?.speaking_opportunity),
      table_or_booth: Boolean(sponsorship?.table_or_booth),
      email_mention: Boolean(sponsorship?.email_mention),
      social_media_mention: Boolean(sponsorship?.social_media_mention),
      website_listing: Boolean(sponsorship?.website_listing),
      attendee_list_access: Boolean(sponsorship?.attendee_list_access),
    });
  }, [open, sponsorship]);

  const setValue = (field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: `1px solid ${BRAND_BLUE}`,
          boxShadow: "0 18px 54px rgba(0,51,108,0.20)",
        },
      }}
    >
      <DialogTitle sx={{ color: BRAND_BLUE, fontWeight: 800 }}>
        {sponsorship?.id ? "Edit Sponsorship" : "Create Sponsorship"}
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#fbfcf8" }}>
        <Box sx={{ pt: 1 }}>
          <SectionHeader>Sponsorship Details</SectionHeader>
          <Box sx={FORM_ROW_SX}>
            <Field span={8}>
              <TextField label="Sponsorship Name" value={values.name} onChange={setValue("name")} fullWidth required sx={FIELD_SX} />
            </Field>
            <Field span={4}>
              <TextField select label="Status" value={values.status} onChange={setValue("status")} fullWidth sx={FIELD_SX}>
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </TextField>
            </Field>
            <Field span={6}>
              <TextField select label="Sponsoring Member" value={values.member_id} onChange={setValue("member_id")} fullWidth required sx={FIELD_SX}>
                {members.map((m) => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
              </TextField>
            </Field>
            <Field span={6}>
              <TextField select label="Event Connection" value={values.event_id} onChange={setValue("event_id")} fullWidth sx={FIELD_SX}>
                <MenuItem value="">No event</MenuItem>
                {events.map((e) => <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>)}
              </TextField>
            </Field>
            <Field span={4}>
              <TextField label="Sponsorship Type" value={values.sponsorship_type} onChange={setValue("sponsorship_type")} fullWidth sx={FIELD_SX} />
            </Field>
            <Field span={4}>
              <TextField label="Sponsorship Level" value={values.sponsorship_level} onChange={setValue("sponsorship_level")} fullWidth sx={FIELD_SX} />
            </Field>
            <Field span={4}>
              <TextField label="Dollar Amount" type="number" value={values.amount} onChange={setValue("amount")} fullWidth sx={FIELD_SX} />
            </Field>
            <Field span={4}>
              <TextField label="Start Date" type="date" value={values.start_date} onChange={setValue("start_date")} fullWidth InputLabelProps={{ shrink: true }} sx={FIELD_SX} />
            </Field>
            <Field span={4}>
              <TextField label="End Date" type="date" value={values.end_date} onChange={setValue("end_date")} fullWidth InputLabelProps={{ shrink: true }} sx={FIELD_SX} />
            </Field>
            <Field span={4}>
              <TextField label="Program Area" value={values.program_area} onChange={setValue("program_area")} fullWidth sx={FIELD_SX} />
            </Field>
          </Box>

          <Box sx={{ mt: 3 }}>
            <SectionHeader>Notes</SectionHeader>
            <TextField label="Notes" value={values.notes} onChange={setValue("notes")} fullWidth multiline minRows={3} sx={FIELD_SX} />
          </Box>

          <Box sx={{ mt: 3 }}>
            <SectionHeader>Benefits</SectionHeader>
            <Box sx={BENEFITS_GRID_SX}>
              {BENEFIT_OPTIONS.map(([key, label]) => (
                <FormControlLabel
                  key={key}
                  sx={BENEFIT_LABEL_SX}
                  control={
                    <Checkbox
                      checked={Boolean(values[key])}
                      onChange={setValue(key)}
                      icon={<RadioButtonUncheckedIcon />}
                      checkedIcon={<CheckCircleIcon />}
                      sx={{ color: BRAND_BLUE, "&.Mui-checked": { color: BRAND_BLUE } }}
                    />
                  }
                  label={label}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={SECONDARY_BUTTON_SX}>Cancel</Button>
        <Button
          onClick={() => onSubmit(payload(values))}
          disabled={saving || !values.name.trim() || !values.member_id}
          sx={PRIMARY_BUTTON_SX}
        >
          {saving ? "Saving..." : "Save Sponsorship"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
